import { useEffect, useRef, useState } from "react";
import { MessageCircleQuestion, X, Send, Loader2 } from "lucide-react";
import { api, streamPost, formatError } from "@/lib/api";

const STARTERS = ["¿Cómo describo bien mi idea?", "¿Cómo funcionan los créditos?", "¿Cómo publico mi aplicación?", "Mi app muestra un error, ¿qué hago?"];

const Rich = ({ text }) => text.replace(/^#+\s*/gm, "").split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 ? <strong key={i} className="text-white">{part}</strong> : part.replace(/^\s*[-*]\s+/gm, "• ")));

export function AssistantWidget({ inline }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);

  useEffect(() => { if (open) api.get("/assistant/messages").then((r) => setMsgs(r.data.reverse())); }, [open]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    const append = (t) => setMsgs((m) => [...m.slice(0, -1), { role: "assistant", content: m[m.length - 1].content + t }]);
    try {
      await streamPost("/assistant/chat", { message: q }, (ev) => {
        if (ev.type === "delta") append(ev.content);
        if (ev.type === "error") append(ev.message);
      });
    } catch (e) { append(e.message || formatError(e)); }
    setBusy(false);
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} data-testid="assistant-toggle-btn" title="Asistente de ayuda"
        className={inline ? "inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" : "fixed bottom-5 right-5 z-50 w-12 h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0A0D14] grid place-items-center shadow-[0_0_30px_rgba(16,185,129,0.45)] transition-transform hover:scale-105"}>
        {open ? <X className="w-4 h-4" /> : <MessageCircleQuestion className="w-4 h-4" />}{inline && <span className="hidden md:inline">Ayuda</span>}
      </button>
      {open && (
        <div className={`fixed ${inline ? "top-16" : "bottom-20"} right-5 z-50 w-[360px] max-w-[calc(100vw-2.5rem)] h-[520px] max-h-[70vh] rounded-2xl border border-[#222B40] bg-[#111622] shadow-2xl flex flex-col rise`} data-testid="assistant-panel">
          <div className="px-4 py-3 border-b border-white/5">
            <p className="text-white font-medium text-sm">Asistente SYNAPSIS</p>
            <p className="text-[11px] text-slate-500">Resuelve dudas en español claro · sin costo de créditos</p>
          </div>
          <div className="flex-1 overflow-y-auto scroll-thin p-3 space-y-3" data-testid="assistant-messages">
            {msgs.length === 0 && (
              <div className="space-y-2">
                {STARTERS.map((s, i) => <button key={s} onClick={() => ask(s)} data-testid={`assistant-starter-${i}`} className="block w-full text-left text-xs px-3 py-2 rounded-lg border border-[#222B40] text-slate-300 hover:border-emerald-500/40">{s}</button>)}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`text-sm whitespace-pre-wrap rounded-xl px-3 py-2 ${m.role === "user" ? "bg-blue-500/15 text-slate-100 ml-8" : "bg-[#161C2E] text-slate-200 mr-4 border border-[#222B40]"}`} data-testid={`assistant-msg-${m.role}`}>
                {m.content ? <Rich text={m.content} /> : <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />}
              </div>
            ))}
            <div ref={end} />
          </div>
          <div className="p-3 border-t border-white/5 flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder="Escribe tu pregunta…" data-testid="assistant-input"
              className="flex-1 rounded-lg bg-[#0A0D14] border border-[#222B40] px-3 text-sm text-slate-100 outline-none focus:border-emerald-500/50" />
            <button onClick={() => ask()} disabled={busy} data-testid="assistant-send-btn" className="w-9 h-9 grid place-items-center rounded-lg bg-emerald-500 text-[#0A0D14] disabled:opacity-50"><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </>
  );
}
